import {
  PRODUCT_IMAGE_MAX_FILE_BYTES,
  PRODUCT_IMAGE_MIME_TYPES,
  PRODUCT_IMAGES_MAX_COUNT,
} from '@erp/shared';
import { Camera, ImagePlus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type PendingImageStatus = 'PENDING' | 'UPLOADING' | 'SUCCESS' | 'FAILED';
export interface PendingImageState {
  readonly status: PendingImageStatus;
  readonly progress: number;
}

function pendingImageKey(file: File): string {
  return `${file.name}:${String(file.size)}:${String(file.lastModified)}`;
}

export function PendingProductImages({
  files,
  onChange,
  disabled,
  states,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled: boolean;
  states: Readonly<Record<string, PendingImageState | undefined>>;
}): JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [previews, setPreviews] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const urls = files.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  const addFiles = (incoming: File[]): void => {
    const remaining = PRODUCT_IMAGES_MAX_COUNT - files.length;
    const valid = incoming.filter(
      (file) =>
        file.size > 0 &&
        file.size <= PRODUCT_IMAGE_MAX_FILE_BYTES &&
        PRODUCT_IMAGE_MIME_TYPES.some((mime) => mime === file.type),
    );
    if (valid.length !== incoming.length || incoming.length > remaining) {
      setError(t('products.images.invalidSelection'));
      return;
    }
    setError(null);
    onChange([...files, ...valid.slice(0, remaining)]);
  };

  return (
    <section className="space-y-3 sm:col-span-2" aria-label={t('products.images.title')}>
      <button
        type="button"
        disabled={disabled}
        className={cn(
          'relative flex min-h-32 w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          dragging ? 'border-primary bg-primary/10' : 'border-border',
        )}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(event) => {
          if (
            !(event.relatedTarget instanceof Node) ||
            !event.currentTarget.contains(event.relatedTarget)
          )
            setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          addFiles([...event.dataTransfer.files]);
        }}
      >
        <ImagePlus className="h-7 w-7" aria-hidden="true" />
        <span className="font-medium">{t('products.images.drop')}</span>
        <span className="text-xs text-muted-foreground">
          {t('products.images.remaining', { count: PRODUCT_IMAGES_MAX_COUNT - files.length })}
        </span>
      </button>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          <ImagePlus className="h-4 w-4" aria-hidden="true" /> {t('products.images.choose')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={() => cameraRef.current?.click()}
          disabled={disabled}
        >
          <Camera className="h-4 w-4" aria-hidden="true" /> {t('products.images.camera')}
        </Button>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={PRODUCT_IMAGE_MIME_TYPES.join(',')}
        className="sr-only"
        onChange={(event) => {
          addFiles([...(event.target.files ?? [])]);
          event.currentTarget.value = '';
        }}
      />
      <input
        ref={cameraRef}
        type="file"
        accept={PRODUCT_IMAGE_MIME_TYPES.join(',')}
        capture="environment"
        className="sr-only"
        onChange={(event) => {
          addFiles([...(event.target.files ?? [])]);
          event.currentTarget.value = '';
        }}
      />
      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {files.length === 0 ? null : (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.lastModified}`}
              className="relative overflow-hidden rounded-md border"
            >
              <img src={previews[index]} alt="" className="aspect-square w-full object-cover" />
              <Button
                type="button"
                size="icon"
                variant="secondary"
                className="absolute right-1 top-1 min-h-11 min-w-11"
                aria-label={t('products.images.removePending', { name: file.name })}
                onClick={() => onChange(files.filter((entry) => entry !== file))}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
              <p className="truncate p-2 text-xs">{file.name}</p>
              <p className="px-2 pb-2 text-xs text-muted-foreground" role="status">
                {t(
                  `products.images.uploadStates.${states[pendingImageKey(file)]?.status ?? 'PENDING'}`,
                  { progress: states[pendingImageKey(file)]?.progress ?? 0 },
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
